import React,{useState,useEffect,useRef,useMemo}from'react';
import{NavLink,useNavigate,useLocation}from'react-router-dom';
import{useAuth}from'../../hooks/useAuth';
import{ROUTE_MODULE,hasAssignedModule,readStoredModules}from'../../constants/modules';

const NAV=[
  {section:'CLINICAL',items:[
    {to:'/dashboard',           icon:'🏠', label:'Dashboard'},
    {to:'/executive-dashboard', icon:'📈', label:'Executive Dashboard'},
    {to:'/patients',            icon:'👤', label:'Patients'},
    {to:'/crm',                 icon:'📇', label:'Enterprise CRM'},
    {to:'/appointments',        icon:'📅', label:'Appointments'},
    {to:'/opd',                 icon:'🩺', label:'OPD Consultation'},
    {to:'/doctor/dashboard',    icon:'👨‍⚕️', label:'Doctor Dashboard'},
    {to:'/doctor-schedule',     icon:'📆', label:'Doctor Schedule'},
    {to:'/cpoe',                icon:'📋', label:'CPOE / Orders'},
    {to:'/ipd',                 icon:'🏥', label:'IPD Admissions'},
    {to:'/bed-board',           icon:'🛏️', label:'Bed Board'},
    {to:'/nursing',             icon:'💊', label:'Nursing'},
    {to:'/indents',             icon:'📦', label:'Nursing Indents'},
    {to:'/emar',                icon:'💉', label:'eMAR'},
    {to:'/icu',                 icon:'❤️', label:'ICU'},
    {to:'/emergency',           icon:'🚨', label:'Emergency'},
    {to:'/ot',                  icon:'🔬', label:'Operation Theatre'},
    {to:'/ophthalmology',       icon:'👁️', label:'Ophthalmology'},
    {to:'/discharge-summary',   icon:'📋', label:'Discharge Summary'},
    {to:'/consents',            icon:'✍️', label:'Consents'},
    {to:'/patient-portal',      icon:'🌐', label:'Patient Portal'},
  ]},
  {section:'DIAGNOSTICS',items:[
    {to:'/laboratory',            icon:'🧪', label:'Laboratory'},
    {to:'/lab/parameter-master',  icon:'🔬', label:'Lab Parameters'},
    {to:'/lab/reference-ranges',  icon:'📊', label:'Reference Ranges'},
    {to:'/lab/culture-templates', icon:'🦠', label:'Culture Templates'},
    {to:'/lab/pathology',         icon:'🔬', label:'Pathology Reports'},
    {to:'/lab/report-master',     icon:'📄', label:'Report Master'},
    {to:'/radiology',             icon:'📡', label:'Radiology / PACS'},
    {to:'/bloodbank',             icon:'🩸', label:'Blood Bank'},
    {to:'/pharmacy',              icon:'💊', label:'Pharmacy'},
    {to:'/terminology',           icon:'📖', label:'Terminology (ICD/SNOMED)'},
  ]},
  {section:'BILLING & FINANCE',items:[
    {to:'/billing',           icon:'🧾', label:'Billing'},
    {to:'/front-office-billing', icon:'💳', label:'Front Office Billing'},
    {to:'/insurance',         icon:'🛡️', label:'Insurance / TPA'},
    {to:'/insurance-preauth', icon:'📝', label:'Insurance Pre-Auth'},
    {to:'/corporate',         icon:'🏢', label:'Corporate Master'},
    {to:'/payers',            icon:'🏦', label:'Payer Management'},
    {to:'/finance',           icon:'📊', label:'Finance'},
  ]},
  {section:'OPERATIONS',items:[
    {to:'/inventory',      icon:'📦', label:'Inventory'},
    {to:'/vendors',        icon:'🚚', label:'Procurement'},
    {to:'/diet',           icon:'🥗', label:'Diet & Kitchen'},
    {to:'/biomedical',     icon:'⚙️', label:'Biomedical'},
    {to:'/cssd',           icon:'🧰', label:'CSSD'},
    {to:'/communications', icon:'💬', label:'Communications'},
  ]},
  {section:'HR & ADMIN',items:[
    {to:'/hrms',      icon:'👥', label:'HRMS'},
    {to:'/roster',    icon:'📆', label:'Duty Roster'},
    {to:'/workflow',  icon:'🔄', label:'Workflow'},
    {to:'/approvals', icon:'✅', label:'Approvals'},
    {to:'/dms',       icon:'📁', label:'Documents'},
  ]},
  {section:'QUALITY & COMPLIANCE',items:[
    {to:'/quality',   icon:'📈', label:'Quality Indicators'},
    {to:'/nabh',      icon:'🏆', label:'NABH Compliance'},
    {to:'/audit',     icon:'🔍', label:'Audit Logs'},
    {to:'/audit-log', icon:'📜', label:'Audit Log Detail'},
  ]},
  {section:'DIGITAL HEALTH',items:[
    {to:'/abdm',        icon:'🔗', label:'ABDM / ABHA'},
    {to:'/fhir',        icon:'⚕️', label:'FHIR R4'},
    {to:'/telemedicine',icon:'📹', label:'Telemedicine'},
  ]},
  {section:'SETTINGS',items:[
    {to:'/settings/branches', icon:'🏥', label:'Branch Management'},
    {to:'/settings/user-stations', icon:'🖥️', label:'User / Station Mapping'},
    {to:'/masters',         icon:'🗂️', label:'System Masters'},
    {to:'/location-master', icon:'🗺️', label:'Location Master'},
    {to:'/charge-master',   icon:'💰', label:'Charge Master'},
    {to:'/settings',        icon:'⚙️', label:'Settings & Users'},
    {to:'/reports',         icon:'📊', label:'Reports'},
    {to:'/analytics',       icon:'📉', label:'Analytics'},
    {to:'/notifications',   icon:'🔔', label:'Notifications'},
  ]},
];
export default function Sidebar(){
  const nav=useNavigate();
  const location=useLocation();
  const {user}=useAuth();
  const navRef=useRef<HTMLElement>(null);
  const [collapsed,setCollapsed]=useState(false);
  const [expandedSections,setExpandedSections]=useState<Record<string,boolean>>(
    Object.fromEntries(NAV.map(s=>[s.section,true]))
  );

  const canSee=(path:string)=>{
    const required=path in ROUTE_MODULE ? ROUTE_MODULE[path] : 'ADMIN';
    return hasAssignedModule(user?.accessibleModules??readStoredModules(),required);
  };

  const visibleNav=useMemo(()=>NAV.map(section=>({
    ...section,
    items:section.items.filter(item=>canSee(item.to)),
  })).filter(section=>section.items.length>0),[user,user?.accessibleModules,user?.role]);

  // Keep the active item's section expanded, and scroll it into view whenever the route changes —
  // otherwise the highlighted item can be scrolled out of sight in this long nav list with no way
  // for the user to tell which page they're on without hunting for it.
  useEffect(()=>{
    const activeSection=visibleNav.find(({items})=>
      items.some(({to})=>location.pathname===to||location.pathname.startsWith(to+'/'))
    )?.section;
    if(activeSection)setExpandedSections(p=>p[activeSection]?p:{...p,[activeSection]:true});
    const t=setTimeout(()=>{
      const activeEl=navRef.current?.querySelector('[aria-current="page"]') as HTMLElement|null;
      if(activeEl&&navRef.current){
        const navEl=navRef.current;
        const target=activeEl.offsetTop-navEl.clientHeight/2+activeEl.clientHeight/2;
        navEl.scrollTop=Math.max(0,target);
      }
    },50);
    return()=>clearTimeout(t);
  },[location.pathname,visibleNav]);

  const branch=localStorage.getItem('branchName')||'Clinqov Meditrack';
  const station=localStorage.getItem('stationName')||'Reception';
  const userName=localStorage.getItem('userName')||'User';
  const userRole=localStorage.getItem('userRole')||'Staff';

  const logout=()=>{
    localStorage.clear();
    nav('/login');
  };

  const toggleSection=(section:string)=>{
    setExpandedSections(p=>({...p,[section]:!p[section]}));
  };

  return(
    <div className={`flex flex-col h-screen ${collapsed?'w-16':'w-64'} transition-all duration-300 flex-shrink-0`}
      style={{background:'linear-gradient(180deg, #02357A 0%, #01285C 100%)'}}>

      {/* ── Logo & Brand ── */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-white/10">
        {!collapsed&&(
          <div className="flex items-center gap-2.5 min-w-0">
            <img src="/logo.png" alt="Clinqov Meditrack" className="w-9 h-9 rounded-lg object-contain bg-white p-0.5 flex-shrink-0"/>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm leading-tight truncate">Clinqov Meditrack</p>
              <p className="text-white/50 text-xs truncate">Private Limited</p>
            </div>
          </div>
        )}
        {collapsed&&<img src="/logo.png" alt="CM" className="w-9 h-9 rounded-lg object-contain bg-white p-0.5 mx-auto"/>}
        <button onClick={()=>setCollapsed(c=>!c)}
          className="text-white/60 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
          title={collapsed?'Expand':'Collapse'}>
          {collapsed?'→':'←'}
        </button>
      </div>

      {/* ── Branch & User info ── */}
      {!collapsed&&(
        <div className="px-3 py-2.5 border-b border-white/10">
          <div className="flex items-center gap-2 bg-white/10 rounded-lg px-2.5 py-2">
            <div className="w-7 h-7 rounded-full bg-[#039089] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {userName[0]?.toUpperCase()||'U'}
            </div>
            <div className="min-w-0">
              <p className="text-white text-xs font-semibold truncate">{userName}</p>
              <p className="text-white/50 text-xs truncate">{userRole}</p>
            </div>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 px-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[#039089] flex-shrink-0"/>
            <span className="text-white/60 text-xs truncate">{branch} · {station}</span>
          </div>
        </div>
      )}

      {/* ── Navigation ── */}
      <nav ref={navRef} className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {visibleNav.map(({section,items})=>(
          <div key={section}>
            {!collapsed&&(
              <button onClick={()=>toggleSection(section)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-white/40 hover:text-white/60 transition-colors">
                <span className="text-[10px] font-bold tracking-widest uppercase">{section}</span>
                <span className="text-xs">{expandedSections[section]?'▾':'▸'}</span>
              </button>
            )}
            {(collapsed||expandedSections[section])&&items.map(({to,icon,label})=>(
              <NavLink key={to} to={to}
                className={({isActive})=>
                  `flex items-center gap-3 mx-2 px-2 py-2 rounded-lg text-sm transition-all ${
                    isActive
                      ? 'bg-[#039089] text-white font-semibold shadow-sm'
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  } ${collapsed?'justify-center':''}` }>
                <span className="text-base flex-shrink-0">{icon}</span>
                {!collapsed&&<span className="truncate text-sm">{label}</span>}
              </NavLink>
            ))}
            {!collapsed&&<div className="h-px bg-white/5 mx-3 my-1"/>}
          </div>
        ))}
      </nav>

      {/* ── Footer ── */}
      <div className="border-t border-white/10 p-3">
        {!collapsed&&(
          <button onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-white/60 hover:bg-red-500/20 hover:text-red-300 transition-colors text-sm">
            <span>🚪</span><span>Sign Out</span>
          </button>
        )}
        {collapsed&&(
          <button onClick={logout} className="w-full flex justify-center py-2 text-white/60 hover:text-red-300" title="Sign Out">🚪</button>
        )}
        {!collapsed&&<p className="text-white/20 text-xs text-center mt-2">Clinqov Meditrack v2.0</p>}
      </div>
    </div>
  );
}
